import { Question, QuestionCreateRequest, QuestionListResponse, FormSchema, FormSchemaCreateRequest, SchemaGenerateRequest, SchemaGenerateResponse, SchemaUpdateRequest, SchemaVersionsResponse, FormSubmission, SubmissionListResponse } from '../types';
import { api } from '../../../../core/utils/apiClient';

export class AdminFormsService {
  // ==========================================
  // QUESTION MANAGEMENT
  // ==========================================

  async getAllQuestions(params?: {
    category?: string;
    is_active?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<Question[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }
      
      const response = await api.get(`/api/v1/forms/questions?${queryParams.toString()}`);
      if (response.ok) {
        const data: QuestionListResponse = await response.json();
        return data.questions || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching questions:', error);
      return [];
    }
  }

  async getQuestionById(questionId: number): Promise<Question | null> {
    try {
      const response = await api.get(`/api/v1/forms/questions/${questionId}`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching question:', error);
      return null;
    }
  }

  async createQuestion(questionData: QuestionCreateRequest): Promise<Question | null> {
    try {
      const response = await api.post('/api/v1/forms/questions', questionData);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error creating question:', error);
      return null;
    }
  }

  async updateQuestion(questionId: number, questionData: Partial<QuestionCreateRequest>): Promise<Question | null> {
    try {
      const response = await api.put(`/api/v1/forms/questions/${questionId}`, questionData);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error updating question:', error);
      return null;
    }
  }

  async deleteQuestion(questionId: number): Promise<boolean> {
    try {
      const response = await api.delete(`/api/v1/forms/questions/${questionId}`);
      return response.ok;
    } catch (error) {
      console.error('Error deleting question:', error);
      return false;
    }
  }

  // ==========================================
  // SCHEMA MANAGEMENT
  // ==========================================

  async getAllSchemas(params?: {
    company_id?: number;
    skip?: number;
    limit?: number;
  }): Promise<FormSchema[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await api.get(`/api/v1/forms/schemas?${queryParams.toString()}`);
      if (response.ok) {
        const data = await response.json();
        return data.schemas || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching schemas:', error);
      return [];
    }
  }

  async getSchemaById(schemaId: number): Promise<FormSchema | null> {
    try {
      const response = await api.get(`/api/v1/forms/schemas/${schemaId}`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching schema:', error);
      return null;
    }
  }

  async createSchema(schemaData: FormSchemaCreateRequest): Promise<FormSchema | null> {
    try {
      const response = await api.post('/api/v1/forms/schemas', schemaData);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error creating schema:', error);
      return null;
    }
  }

  async generateSchema(generateData: SchemaGenerateRequest): Promise<SchemaGenerateResponse | null> {
    try {
      const response = await api.post('/api/v1/forms/schemas/generate', generateData);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error generating schema:', error);
      return null;
    }
  }

  async updateSchema(schemaId: number, schemaData: SchemaUpdateRequest): Promise<FormSchema | null> {
    try {
      const response = await api.put(`/api/v1/forms/schemas/${schemaId}`, schemaData);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error updating schema:', error);
      return null;
    }
  }

  async getSchemaVersions(schemaId: number): Promise<SchemaVersionsResponse | null> {
    try {
      const response = await api.get(`/api/v1/forms/schemas/${schemaId}/versions`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching schema versions:', error);
      return null;
    }
  }

  // ==========================================
  // SUBMISSION MANAGEMENT
  // ==========================================

  async getAllSubmissions(params?: {
    schema_id?: number;
    company_id?: number;
    skip?: number;
    limit?: number;
  }): Promise<FormSubmission[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await api.get(`/api/v1/forms/submissions?${queryParams.toString()}`);
      if (response.ok) {
        const data: SubmissionListResponse = await response.json();
        return data.submissions || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching submissions:', error);
      return [];
    }
  }

  async getSubmissionById(submissionId: number): Promise<FormSubmission | null> {
    try {
      const response = await api.get(`/api/v1/forms/submissions/${submissionId}`);
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch (error) {
      console.error('Error fetching submission:', error);
      return null;
    }
  }

  async enrichSubmission(submissionId: number): Promise<boolean> {
    try {
      const response = await api.post(`/api/v1/forms/submissions/${submissionId}/enrich`);
      return response.ok;
    } catch (error) {
      console.error('Error enriching submission:', error);
      return false;
    }
  }
}

export const adminFormsService = new AdminFormsService();
